import React from 'react';
import { LuListTree } from 'react-icons/lu';
import Markdown from '../ui/Markdown';
import { analysisContent, toEntries } from './analysisContent.js';

/** DOM id of a category, shared by the sections and the outline that scrolls to them. */
export const sectionAnchor = (analysisType, index) => `analysis-${String(analysisType).replace(/[^a-zA-Z0-9_-]/g, '')}-${index}`;

/** Category card: accent bar + title + point count, actions on the right, entries below. */
export const SectionShell = ({ id, title, count, actions = null, children }) => (
  <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4 rounded-xl border border-gray-200 bg-white dark:border-gray-900 dark:bg-night-800">
    <header className="flex items-center gap-2.5 rounded-t-xl border-b border-gray-100 bg-gray-50/70 px-4 py-2.5 dark:border-gray-900 dark:bg-night-850">
      <span className="h-4 w-1 flex-shrink-0 rounded-full bg-brand-500" aria-hidden />
      <div className="min-w-0 flex-1">{title}</div>
      {count !== null && count !== undefined && (
        <span className="flex-shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{count} point{count > 1 ? 's' : ''}</span>
      )}
      {actions}
    </header>
    {children}
  </section>
);

/** Read-only analysis: one card per category, one entry per paragraph block. */
export const AnalysisDisplay = ({ analysis }) => {
  const categories = Object.entries(analysisContent(analysis));
  if (categories.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Cette analyse ne contient aucun élément.</p>;
  }
  return (
    <div className="space-y-4">
      {categories.map(([category, value], index) => {
        const entries = toEntries(value);
        const id = sectionAnchor(analysis.type, index);
        return (
          <SectionShell
            key={category}
            id={id}
            count={entries.length}
            title={<h3 id={`${id}-title`} className="text-sm font-semibold text-gray-900 dark:text-white">{category}</h3>}
          >
            <ul className="divide-y divide-gray-100 dark:divide-gray-900">
              {entries.map((entry, entryIndex) => (
                <li key={entryIndex} className="px-4 py-3.5">
                  <Markdown>{entry}</Markdown>
                </li>
              ))}
            </ul>
          </SectionShell>
        );
      })}
    </div>
  );
};

/** Sticky outline of the categories (wide screens): jumps to a section of a long analysis. */
export const AnalysisOutline = ({ analysis }) => {
  const categories = Object.keys(analysisContent(analysis));
  if (categories.length < 3) return null;
  const jump = (index) => {
    document.getElementById(sectionAnchor(analysis.type, index))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <nav aria-label="Sommaire de l'analyse" className="sticky top-4 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-900 dark:bg-night-800">
      <p className="mb-2 flex items-center gap-1.5 px-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        <LuListTree className="h-3.5 w-3.5" aria-hidden /> Sommaire
      </p>
      <ol className="space-y-0.5">
        {categories.map((category, index) => (
          <li key={category}>
            <button
              type="button"
              onClick={() => jump(index)}
              className="w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-[13px] leading-snug text-gray-600 outline-none hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white"
            >
              {category}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default AnalysisDisplay;
